import { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, X, Download, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

export function Lightbox({ items, index, onClose, onNav }) {
  const [downloading, setDownloading] = useState(false);
  const touchX = useRef(null);
  const item = items[index];

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onNav(1);
      if (e.key === "ArrowLeft") onNav(-1);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose, onNav]);

  const download = async (e) => {
    e.stopPropagation();
    if (!item?.id) return;
    setDownloading(true);
    try {
      const { data } = await api.get(`/gallery/photos/${item.id}/download`);
      const a = document.createElement("a");
      a.href = data.url;
      a.download = data.filename || "foto.jpg";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch {
      window.open(item.url, "_blank");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <motion.div
      data-testid="galerias-lightbox"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      className="fixed inset-0 z-[120] bg-black/92 backdrop-blur-sm flex items-center justify-center p-3 sm:p-8"
    >
      <div className="absolute top-4 right-4 flex items-center gap-2 z-10">
        {item?.id && (
          <button
            data-testid="lightbox-download"
            onClick={download}
            aria-label="Descargar"
            className="h-11 w-11 sm:h-12 sm:w-12 rounded-full bg-white/10 hover:bg-gold hover:text-navy text-white flex items-center justify-center transition-colors"
          >
            {downloading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Download className="h-5 w-5" />}
          </button>
        )}
        <button
          data-testid="galerias-lightbox-close"
          onClick={onClose}
          aria-label="Cerrar"
          className="h-11 w-11 sm:h-12 sm:w-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
        >
          <X className="h-6 w-6" />
        </button>
      </div>

      <button
        onClick={(e) => { e.stopPropagation(); onNav(-1); }}
        aria-label="Anterior"
        className="absolute left-2 sm:left-6 h-11 w-11 sm:h-12 sm:w-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
      >
        <ChevronLeft className="h-6 w-6" />
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); onNav(1); }}
        aria-label="Siguiente"
        className="absolute right-2 sm:right-6 h-11 w-11 sm:h-12 sm:w-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
      >
        <ChevronRight className="h-6 w-6" />
      </button>

      <motion.figure
        key={index}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
        onTouchEnd={(e) => {
          if (touchX.current === null) return;
          const dx = e.changedTouches[0].clientX - touchX.current;
          touchX.current = null;
          if (dx < -60) onNav(1);
          else if (dx > 60) onNav(-1);
        }}
        onClick={(e) => e.stopPropagation()}
        className="max-w-5xl w-full"
      >
        <img
          src={item.url}
          alt={item.alt}
          draggable="false"
          className="w-full max-h-[78vh] object-contain rounded-xl select-none"
        />
        <figcaption className="mt-4 text-center text-white/80 text-sm">
          {item.alt}
          <span className="ml-3 text-white/40">{index + 1} / {items.length}</span>
        </figcaption>
      </motion.figure>
    </motion.div>
  );
}
